'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useViewport } from 'src/hooks/useViewport';

const images = [
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/8d7689b4-ea52-4599-f487-47c372e73b00/original',
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/bebf0ebb-6dec-434b-0b6a-2c7073eba000/original',
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/772d403e-1547-4bec-1255-f5b3c1199400/original',
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/f3508299-5839-4fde-d184-24f6356c9500/original',
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/670acc66-c306-4fde-5903-74f8f33fdf00/original',
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/be1131d0-b0d2-454c-e6b3-1c65440e5100/original',
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/d99a5198-819e-416a-cc22-7aa005110f00/original',
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/25c9bf04-eec2-4e0f-157a-7fbe02fe6c00/original',
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/43fc849b-ce60-44ed-c5c5-66d521d95100/original',
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/24250d96-8298-48d7-9955-128db56e6a00/original',
  'https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/e991daa3-dfb9-496b-3ab0-172ef2860900/original',
];

export default function MeetBoomersSection() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previousImages, setPreviousImages] = useState<string[]>([]);
  const [visibleImages, setVisibleImages] = useState<string[]>([]);
  const { isMobile, isClient } = useViewport();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 4) % 11);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const availableImages = images.filter(
      (img) => !previousImages.includes(img),
    );
    const newVisibleImages = [];

    for (let i = 0; i < 4; i++) {
      const index = (currentImageIndex + i) % availableImages.length;
      newVisibleImages.push(availableImages[index]);
    }

    setVisibleImages(newVisibleImages);
    setPreviousImages(newVisibleImages);
  }, [currentImageIndex]);

  return (
    <section className='relative overflow-hidden bg-blue py-20'>
      <div className="absolute inset-0 bg-gradient-to-b from-blue-600 via-blue-700 to-blue-800" />

      <div className="absolute inset-0 opacity-30">
        <svg
          className='absolute inset-0 h-full w-full'
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="community-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.7" />
              <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#2563eb" stopOpacity="0.7" />
            </linearGradient>
            <radialGradient
              id="community-orb"
              cx="50%"
              cy="50%"
              r="50%"
              fx="50%"
              fy="50%"
            >
              <stop offset="0%" stopColor="#93c5fd" stopOpacity="1" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </radialGradient>
            <filter
              id="community-glow"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feGaussianBlur stdDeviation="20" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            <pattern
              id="waves"
              width="100"
              height="30"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M0 15 Q 25 5, 50 15 T 100 15"
                fill="none"
                stroke="#60a5fa"
                strokeWidth="2"
              />
              <path
                d="M0 15 Q 25 25, 50 15 T 100 15"
                fill="none"
                stroke="#93c5fd"
                strokeWidth="1.5"
              />
            </pattern>
            <pattern
              id="dots"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="10" cy="10" r="1.5" fill="#93c5fd" />
            </pattern>
          </defs>

          <rect x="0" y="0" width="100%" height="100%" fill="#1e40af" />
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="url(#waves)"
            opacity="0.15"
          />
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="url(#dots)"
            opacity="0.2"
          />

          <circle
            cx="20%"
            cy="30%"
            r="10%"
            fill="url(#community-orb)"
            filter="url(#community-glow)"
            opacity="0.6"
          />
          <circle
            cx="80%"
            cy="70%"
            r="15%"
            fill="url(#community-orb)"
            filter="url(#community-glow)"
            opacity="0.7"
          />
          <circle
            cx="70%"
            cy="25%"
            r="8%"
            fill="url(#community-orb)"
            filter="url(#community-glow)"
            opacity="0.5"
          />

          <path
            d="M 20% 30% L 80% 70% L 70% 25% Z"
            fill="none"
            stroke="#60a5fa"
            strokeWidth="1.5"
            opacity="0.3"
          />
          <path
            d="M 20% 30% Q 45% 45% 70% 25%"
            fill="none"
            stroke="#93c5fd"
            strokeWidth="2"
            opacity="0.2"
          />
          <path
            d="M 80% 70% Q 75% 45% 70% 25%"
            fill="none"
            stroke="#93c5fd"
            strokeWidth="2"
            opacity="0.2"
          />

          <circle cx="30%" cy="60%" r="1.5%" fill="#93c5fd" opacity="0.3" />
          <circle cx="40%" cy="20%" r="1%" fill="#93c5fd" opacity="0.4" />
          <circle cx="60%" cy="50%" r="1.2%" fill="#93c5fd" opacity="0.3" />
          <circle cx="75%" cy="40%" r="0.8%" fill="#93c5fd" opacity="0.5" />
          <circle cx="50%" cy="75%" r="1.3%" fill="#93c5fd" opacity="0.4" />

          <path
            d="M 30% 60% L 60% 50% L 50% 75% Z"
            fill="none"
            stroke="#60a5fa"
            strokeWidth="1"
            opacity="0.2"
          />
          <path
            d="M 40% 20% L 75% 40% L 60% 50% Z"
            fill="none"
            stroke="#60a5fa"
            strokeWidth="1"
            opacity="0.2"
          />
        </svg>
      </div>

      <div className='pointer-events-none absolute right-0 bottom-0 z-0 select-none overflow-hidden'>
        <div className="relative">
          <img
            src="https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/d74b4de8-8471-4e72-30fd-a1bf23793f00/original"
            alt="Boomers Grilling"
            className={`translate-x-[10%] translate-y-[10%] object-contain opacity-75 ${
              isClient && !isMobile ? 'w-[550px] lg:w-[650px]' : 'w-[300px]'
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-tl from-blue-800/10 via-transparent to-transparent" />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent" />

      <div className='relative mx-auto max-w-6xl px-4'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className='mb-16 text-center'
        >
          <h1 className='mb-6 font-bold text-4xl text-white'>
            MEET THE BOOMERS
          </h1>
        </motion.div>

        <div
          className={`grid items-center gap-12 ${isClient && !isMobile ? 'grid-cols-2' : 'grid-cols-1'}`}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className='mx-auto grid max-w-2xl grid-cols-2 gap-4'>
              {[0, 1, 2, 3].map((position) => (
                <div
                  key={position}
                  className='glass-effect group aspect-square overflow-hidden rounded-xl transition-all duration-300 hover:shadow-blue-500/5 hover:shadow-xl'
                >
                  <AnimatePresence mode="sync">
                    <motion.div
                      key={visibleImages[position]}
                      className="absolute inset-0"
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: 1,
                        transition: {
                          duration: 1.2,
                          delay: position * 0.2,
                          ease: 'easeInOut',
                        },
                      }}
                      exit={{
                        opacity: 0,
                        transition: {
                          duration: 0.8,
                          ease: 'easeInOut',
                        },
                      }}
                      whileHover={{
                        scale: 1.02,
                        transition: { duration: 0.2 },
                      }}
                    >
                      <img
                        src={visibleImages[position]}
                        alt={`Boomer ${position + 1}`}
                        className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
                      />
                      <div className='absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100' />
                    </motion.div>
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={`text-center ${isClient && !isMobile ? 'text-left' : ''} space-y-6`}
          >
            <div className='rounded-xl border border-blue-400/20 bg-blue-900/50 p-6 backdrop-blur-sm md:p-8'>
              <h2 className='mb-4 font-bold text-3xl text-white'>
                <span className="bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  A Community of Boomers
                </span>
              </h2>
              <p className='text-blue-100 text-xl leading-relaxed drop-shadow-sm'>
                Become part of a vibrant community dedicated to demystifying the
                world of cryptocurrency for Boomers. By joining us, you'll gain
                access to resources, live discussions, and expert advice
                tailored specifically for those new to digital currencies.
              </p>
              <div className="mt-6">
                <Link
                  href="https://t.co/wkSI8Su1Da"
                  target="_blank"
                  className="btn btn-primary inline-flex items-center"
                >
                  Join us <i className="fa-brands fa-telegram ml-2" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
